import React from 'react';
import { cleanResumeText, normalizeResumeBulletLines } from './resumeBulletUtils';
import {
  buildSkillContextFromForm,
  optimizeSkillsForResume,
  randomizeSkills,
} from './skillCuration';
import { isFederalResume } from './resumeFormat';
import displayValues from '../json/displayValues.json';
import hiringAuthorities from './hiringAuthorities';
import { formatResumeDateRange } from './resumeDate';

// Fallback section order if formData.sectionOrder is missing
const DEFAULT_ORDER = [
  'personalInfo',
  'professionalSummary',
  'skills',
  'workHistory',
  'educationHistory',
  'certifications',
  'training',
  'volunteerWork',
  'languages',
  // Insert interests/hobbies and awards/honors after languages
  'interests',
  'awards',
  'organizations',
  'references',
  'additionalInformation',
  'militaryInfo',
];

// --- Sanitizers / guards ---
const cleanText = (v) => {
  return cleanResumeText(v);
};
const isNonEmptyArray = (v) => Array.isArray(v) && v.length > 0;
const isNonEmptyString = (v) => cleanText(v).length > 0;
const displayValue = (group, value) =>
  cleanText(displayValues?.[group]?.[value] || value);
const displayHiringAuthorities = (authorityCodes = []) => {
  if (!Array.isArray(authorityCodes)) return [];
  return authorityCodes
    .map((code) => {
      const match = hiringAuthorities.find((authority) => authority.value === code);
      return cleanText(match?.displayValue || code);
    })
    .filter(Boolean);
};

// --- Minimal inline editors (self-contained) ---
const InlineText = ({ value, onChange, placeholder, disabled }) => {
  const [txt, setTxt] = React.useState(cleanText(value));
  React.useEffect(() => { setTxt(cleanText(value)); }, [value]);
  return (
    <input
      type="text"
      value={txt}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(e) => setTxt(e.target.value)}
      onBlur={() => onChange?.(cleanText(txt))}
      className="themed-textarea"
    />
  );
};

const InlineArea = ({ value, onChange, placeholder, disabled, rows = 4 }) => {
  const [txt, setTxt] = React.useState(cleanText(value));
  React.useEffect(() => { setTxt(cleanText(value)); }, [value]);
  return (
    <textarea
      rows={rows}
      value={txt}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(e) => setTxt(e.target.value)}
      onBlur={() => onChange?.(cleanText(txt))}
      className="themed-textarea"

    />
  );
};

// NEW: per-item list editor (add/remove/edit items individually)
const InlineListEditor = ({ value = [], onChange, placeholder = 'New item' }) => {
  const items = (value || []).map(cleanText);

  const updateItem = (idx, val) => {
    const next = [...items];
    next[idx] = cleanText(val);
    onChange?.(next.filter(Boolean));
  };

  const addItem = () => {
    onChange?.([...items, '']);
  };

  const removeItem = (idx) => {
    const next = items.filter((_, i) => i !== idx);
    onChange?.(next);
  };

  return (
    <div style={{ display: 'grid', gap: 6 }}>
      {items.map((it, idx) => (
        <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 6 }}>
          <input
            type="text"
            value={it}
            placeholder={placeholder}
            onChange={(e) => updateItem(idx, e.target.value)}
            onBlur={(e) => updateItem(idx, e.target.value)}
            className="themed-input"
          />
          <button type="button" onClick={() => removeItem(idx)} aria-label="Remove item">🗑</button>
        </div>
      ))}
      <button type="button" onClick={addItem}>+ Add</button>
    </div>
  );
};

const InlineBullets = ({ value = [], onChange, disabled }) => {
  const [lines, setLines] = React.useState((value || []).map(cleanText).filter(Boolean).join('\n'));
  React.useEffect(() => {
    setLines((value || []).map(cleanText).filter(Boolean).join('\n'));
  }, [value]);
  const rows = Math.max(3, lines.split('\n').length);
  return (
    <textarea
      rows={rows}
      disabled={disabled}
      value={lines}
      onChange={(e) => setLines(e.target.value)}
      onBlur={() => {
        const arr = lines.split('\n').map(cleanText).filter(Boolean);
        onChange?.(arr);
      }}
      className="themed-input"
      placeholder="One bullet per line"
    />
  );
};

// Display mappings for military values → labels
const STATUS_LABELS = {
  activeDuty: 'Active Duty',
  veteran: 'Veteran',
  reservist: 'Reservist',
  nationalGuard: 'National Guard',
  noMilitaryService: 'No Military Service',
};

const BRANCH_LABELS = {
  army: 'Army',
  navy: 'Navy',
  airForce: 'Air Force',
  marines: 'Marine Corps',
  coastGuard: 'Coast Guard',
  spaceForce: 'Space Force',
};

const toLabel = (val, map) => {
  const v = cleanText(val);
  return (v && map[v]) || v || '';
};

const Header = ({ personalInfo, federal }) => {
  if (!personalInfo) return null;
  const {
    firstName, lastName, email, phoneNumber, socialPresence, street,
    city, state, postalCode, country
  } = personalInfo;

  const name = [cleanText(firstName), cleanText(lastName)].filter(Boolean).join(' ');
  const location = (federal
    ? [street, city, state, postalCode, country]
    : [city, state, country]
  ).map(cleanText).filter(Boolean).join(' | ');
  const authorityDisplayNames = federal
    ? displayHiringAuthorities(personalInfo.authority)
    : [];

  return (
<header className="resume-header" id="resume">
  {name && <h2 className="name-large">{name}</h2>}
  <div className="uniform-line-spacing-personal">
    {(isNonEmptyString(email) || isNonEmptyString(phoneNumber) || isNonEmptyString(socialPresence)) && (
      <div>
        {[email, phoneNumber, socialPresence]
          .filter(isNonEmptyString)
          .map((item, idx, arr) => (
            <span key={idx}>
              {cleanText(item)}
              {idx < arr.length - 1 && ' | '}
            </span>
          ))}
      </div>
    )}

    {isNonEmptyString(location) && <div>{location}</div>}
    {authorityDisplayNames.length > 0 && (
      <div><strong>Hiring Authorities:</strong> {authorityDisplayNames.join(' | ')}</div>
    )}
  </div>

  {/* spacer */}
  <div style={{ height: '1em' }} />
</header>
  );
};

const Section = ({ title, children }) => {
  if (!children) return null;
  if (Array.isArray(children) && children.length === 0) return null;
  return (
    <section className="resume-section">
      {title && <h3>{title}</h3>}
      {children}
    </section>
  );
};

// ---- Helpers ----
const renderProfessionalSummary = (summary, editable, setFormData, formData) => {
  if (!formData?.showProfessionalSummary) return null; 

  if (editable) {
    return (
      <InlineArea
        value={summary}
        placeholder="2–3 line summary that highlights your value"
        onChange={(val) => setFormData?.({ ...formData, professionalSummary: val })}
      />
    );
  }
  const txt = cleanText(summary);
  if (!txt) return null;
  return <p>{txt}</p>;
};

// Pipe-separated inline renderer (ATS-friendly, sanitized)
const renderPipeSeparated = (arr = []) => {
  if (!isNonEmptyArray(arr)) return null;
  const items = arr.map(cleanText).filter((s) => s.length > 0);
  if (items.length === 0) return null;
  return <div className="pipe-separated-list">{items.join(' | ')}</div>;
};

// Skills: per-item editing when editable, pipe-separated otherwise
const renderSkills = (skills, editable, setFormData, formData) => {
  if (editable) {
    const updateSkills = (nextSkills) => setFormData?.({
      ...formData,
      skills: nextSkills,
      selectedSkills: nextSkills,
    });
    const optimizeCurrentSkills = () => updateSkills(
      optimizeSkillsForResume(
        formData?.selectedSkills?.length ? formData.selectedSkills : skills,
        buildSkillContextFromForm(formData),
        formData,
      ),
    );
    const randomizeCurrentSkills = () => updateSkills(
      randomizeSkills(formData?.selectedSkills?.length ? formData.selectedSkills : skills),
    );
    return (
      <div style={{ display: 'grid', gap: 8 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <button type="button" onClick={optimizeCurrentSkills}>
            Optimize Skills
          </button>
          <button type="button" onClick={randomizeCurrentSkills}>
            Randomize Skills
          </button>
        </div>
        <InlineListEditor
          value={skills}
          placeholder="Skill"
          onChange={(arr) => updateSkills(arr)}
        />
      </div>
    );
  }
  return renderPipeSeparated(skills);
};

// Optional: also give per-item editing to languages/training/orgs for parity
const renderLanguages = (languages, editable, setFormData, formData) => {
  if (editable) {
    return (
      <InlineListEditor
        value={languages}
        placeholder="Language"
        onChange={(arr) => setFormData?.({ ...formData, languages: arr })}
      />
    );
  }
  return renderPipeSeparated(languages);
};

// Render interests and hobbies: per-item editing when editable, pipe-separated otherwise
const renderInterests = (interests, editable, setFormData, formData) => {
  if (editable) {
    return (
      <InlineListEditor
        value={interests}
        placeholder="Interest or Hobby"
        onChange={(arr) => setFormData?.({ ...formData, interests: arr })}
      />
    );
  }
  return renderPipeSeparated(interests);
};

// Render awards and honors: per-item editing when editable, pipe-separated otherwise
const renderAwards = (awards, editable, setFormData, formData) => {
  if (editable) {
    return (
      <InlineListEditor
        value={awards}
        placeholder="Award or Honor"
        onChange={(arr) => setFormData?.({ ...formData, awards: arr })}
      />
    );
  }
  return renderPipeSeparated(awards);
};

const renderTraining = (training, editable, setFormData, formData) => {
  if (editable) {
    return (
      <InlineListEditor
        value={training}
        placeholder="Training"
        onChange={(arr) => setFormData?.({ ...formData, training: arr })}
      />
    );
  }
  return renderPipeSeparated(training);
};

const renderOrganizations = (orgs, editable, setFormData, formData) => {
  if (editable) {
    return (
      <InlineListEditor
        value={orgs}
        placeholder="Organization"
        onChange={(arr) => setFormData?.({ ...formData, organizations: arr })}
      />
    );
  }
  return renderPipeSeparated(orgs);
};

// Certifications: normalize to readable labels; keep read-only in inline mode for now
const renderCertifications = (certs /*, editable, setFormData, formData */) => {
  const normalized = (certs || []).map((c) => {
    if (typeof c === 'string') return cleanText(c);
    const name = cleanText(c?.certificationName);
    const auth = cleanText(c?.certificationAuthority);
    const issue = cleanText(c?.issueDate);
    const expiry = cleanText(c?.expiryDate);
    const showExpiry = c?.showExpiryDate !== false;
    let label = name;
    if (auth) label += (label ? ' — ' : '') + auth;
    let dates = issue || '';
    if (showExpiry && expiry) dates = dates ? `${dates} to ${expiry}` : expiry;
    if (dates) label += (label ? ` (${dates})` : dates);
    return label;
  });
  return renderPipeSeparated(normalized);
};

// Prefer enhanced arrays → bullet arrays → legacy strings (all sanitized)
const collectWorkBullets = (job) => {
  const enhanced = [
    ...(isNonEmptyArray(job?.enhancedResponsibilities) ? job.enhancedResponsibilities : []),
    ...(isNonEmptyArray(job?.enhancedAccomplishments) ? job.enhancedAccomplishments : []),
  ];
  const normalizedEnhanced = normalizeResumeBulletLines(enhanced);
  if (normalizedEnhanced.length) return normalizedEnhanced;

  const bullets = [
    ...(isNonEmptyArray(job?.responsibilitiesBullets) ? job.responsibilitiesBullets : []),
    ...(isNonEmptyArray(job?.accomplishmentsBullets) ? job.accomplishmentsBullets : []),
  ];
  const normalizedBullets = normalizeResumeBulletLines(bullets);
  if (normalizedBullets.length) return normalizedBullets;

  const legacy = [
    ...(isNonEmptyString(job?.responsibilities) ? [job.responsibilities] : []),
    ...(isNonEmptyString(job?.keyAccomplishments) ? [job.keyAccomplishments] : []),
  ];
  return normalizeResumeBulletLines(legacy);
};

// Vertical-bullet sections (work/education/volunteer/references)
const renderWorkHistory = (workHistory = [], editable, setFormData, formData, federal) => {
  const visibleWorkHistory = workHistory.filter(
    (job) => job?.showOnResume !== false,
  );
  if (!isNonEmptyArray(visibleWorkHistory)) return null;
  return (
    <div>
      {visibleWorkHistory.map((job) => {
        if (!job) return null;
        const idx = workHistory.indexOf(job);
        const jobTitle = cleanText(job.jobTitle);
        const employer = cleanText(job.employer);
        const employerAddress = cleanText(job.employerAddress);
        const hoursPerWeek = cleanText(job.hoursPerWeek);
        const payAmount = cleanText(job.payAmount);
        const payRate = displayValue('payRate', job.payRate);
        const employmentType = displayValue('employmentType', job.employmentType);
        const startDate = cleanText(job.startDate);
        const endDate = cleanText(job.endDate);
        const present = !!job.present;
        const dateRange = formatResumeDateRange(startDate, endDate, present);

        // Supervisor info
        const supervisorName = cleanText(job.supervisor);
        const supervisorContact = cleanText(job.supervisorContact);
        const canContact = cleanText(job.canContactSupervisor);
        let contactLabel = '';
        if (canContact.toLowerCase() === 'yes') {
          contactLabel = ' (May Contact)';
        } else if (canContact.toLowerCase() === 'no') {
          contactLabel = ' (Please Do Not Contact)';
        }
        const supervisorLine = supervisorName
          ? `Supervisor: ${supervisorName}${supervisorContact ? ` (${supervisorContact})` : ''}${contactLabel}`
          : null;

        // Inline pipe-separated header (title, employer, address, dates)
        const parts = [];
        if (jobTitle || employer) {
          parts.push([jobTitle, employer].filter(Boolean).join(' — '));
        }
        if (federal && employerAddress) parts.push(employerAddress);
        if (dateRange) parts.push(dateRange);
        const headerLine = parts.join(' | ');

        // Collect bullets
        const bullets = collectWorkBullets(job);

        return (
          <div key={idx} style={{ marginBottom: 8 }}>
            {editable ? (
              <>
                <InlineText value={jobTitle} onChange={(val) => {
                  const copy = [...workHistory];
                  copy[idx] = { ...copy[idx], jobTitle: val };
                  setFormData?.({ ...formData, workHistory: copy });
                }} placeholder="Job Title" />
                {employer && <div><em>{employer}</em></div>}
              </>
            ) : (
              isNonEmptyString(headerLine) && <div><strong>{headerLine}</strong></div>
            )}

            {/* Hours per week */}
            {!editable && federal && isNonEmptyString(hoursPerWeek) && (
              <div>{`Hours/Week: ${hoursPerWeek}`}</div>
            )}

            {!editable && federal && (isNonEmptyString(payAmount) || isNonEmptyString(employmentType)) && (
              <div>
                {[
                  employmentType ? `Employment Type: ${employmentType}` : '',
                  payAmount ? `Pay: ${payAmount}${payRate ? ` ${payRate}` : ''}` : '',
                ].filter(Boolean).join(' | ')}
              </div>
            )}

            {/* Supervisor info */}
            {!editable && federal && isNonEmptyString(supervisorLine) && <div>{supervisorLine}</div>}

            {editable ? (
              <>
                <div style={{ marginTop: 6, marginBottom: 4 }}><em>Bullets (one per line)</em></div>
                <InlineBullets
                  value={bullets}
                  onChange={(arr) => {
                    const copy = [...workHistory];
                    copy[idx] = { ...copy[idx], enhancedResponsibilities: arr };
                    setFormData?.({ ...formData, workHistory: copy });
                  }}
                />
              </>
            ) : (
              isNonEmptyArray(bullets) && (
                <ul>
                  {bullets.map((b, i) => <li key={i}>{b}</li>)}
                </ul>
              )
            )}
          </div>
        );
      })}
    </div>
  );
};

const renderEducation = (educationHistory = [], editable, setFormData, formData) => {
  const visibleEducationHistory = educationHistory.filter(
    (entry) => entry?.showOnResume !== false,
  );
  if (!isNonEmptyArray(visibleEducationHistory)) return null;

  return (
    <div>
      {visibleEducationHistory.map((ed) => {
        if (!ed) return null;
        const idx = educationHistory.indexOf(ed);
        const degree = cleanText(ed.degree);
        const institution = cleanText(ed.institution);
        const startDate = cleanText(ed.startDate);
        const endDate = cleanText(ed.endDate);
        const gpa = cleanText(ed.gpa);
        const minor = cleanText(ed.minor);
        const showGPA = ed.showGPA !== false;
        const showMinor = ed.showMinor !== false;

        const titleLine = [
          degree,
          institution,
          isNonEmptyString(ed.major) ? `Major: ${cleanText(ed.major)}` : null,
          showMinor && isNonEmptyString(minor) ? `Minor: ${minor}` : null,
          showGPA && isNonEmptyString(gpa) ? `GPA: ${gpa}` : null,
          isNonEmptyString(ed.graduationYear) ? `Graduation Year: ${cleanText(ed.graduationYear)}` : null
        ].filter(Boolean).join(' | ');

        const dateLine = formatResumeDateRange(startDate, endDate, !!ed.present);

        // Courses
        const courses = isNonEmptyArray(ed.relevantCoursesBullets)
          ? ed.relevantCoursesBullets
          : (isNonEmptyArray(ed.relevantCourses) ? ed.relevantCourses : []);

        // Projects
        // If the caller has provided an array of pre‑enhanced projects on the entry itself,
        // honour that first. Otherwise fall back to the significantProjects array and
        // gracefully handle either objects (with text/enhancedProject fields) or plain
        // strings/numbers. Without this fallback, significantProjects entered through
        // the form (which are simple strings) would never render because the previous
        // implementation expected an object with a `text` property. See issue report
        // from user (projects were not showing up).
        let projects = [];
        if (isNonEmptyArray(ed.enhancedProjects)) {
          projects = ed.enhancedProjects.map(cleanText).filter(Boolean);
        } else if (isNonEmptyArray(ed.significantProjects)) {
          projects = ed.significantProjects
            .map((p) => {
              // p may be a plain string/number or an object with fields
              if (p == null) return '';
              if (typeof p === 'string' || typeof p === 'number') {
                return cleanText(p);
              }
              // Attempt to use enhancedProject, fallback to text, fallback to toString
              return cleanText(p.enhancedProject || p.text || p);
            })
            .filter((txt) => isNonEmptyString(txt));
        }

        const setCourses = (arr) => {
          const copy = [...educationHistory];
          copy[idx] = { ...copy[idx], relevantCoursesBullets: arr };
          setFormData?.({ ...formData, educationHistory: copy });
        };

        const setProjects = (arr) => {
          // When editing projects in the resume, convert the array of strings back to
          // the expected internal format: each entry becomes an object with a `text`
          // property. This allows later enhancements (AI) to add an `enhancedProject`
          // field. Previously, missing this wrapper caused project strings to be
          // overlooked during rendering. See comment above for details.
          const copy = [...educationHistory];
          copy[idx] = {
            ...copy[idx],
            significantProjects: arr.map((txt) => ({ text: txt }))
          };
          setFormData?.({ ...formData, educationHistory: copy });
        };

        const coursesInline = courses.map(cleanText).filter(Boolean).join(' | ');

        return (
          <div key={idx} style={{ marginBottom: 8 }}>
            {isNonEmptyString(titleLine) && <div><strong>{titleLine}</strong></div>}
            {isNonEmptyString(dateLine) && <div className="right-align">{dateLine}</div>}

            {/* Courses */}
            {editable ? (
              <>
                <div style={{ marginTop: 6, marginBottom: 4 }}><em>Relevant Courses</em></div>
                <InlineListEditor value={courses} onChange={setCourses} placeholder="Course" />
              </>
            ) : (
              isNonEmptyString(coursesInline) && <div className="pipe-separated-list"> Courses: {coursesInline}</div>
            )}

            {/* Projects */}
            {editable ? (
              <>
                <div style={{ marginTop: 6, marginBottom: 4 }}><em>Projects (one per line)</em></div>
                <InlineBullets
                  value={projects}
                  onChange={(newArr) => setProjects(newArr)}
                />
              </>
            ) : (
              isNonEmptyArray(projects) && (
                <>
                  <div><em>Projects:</em></div>
                  <ul>
                    {projects.map((p, i) => <li key={i}>{p}</li>)}
                  </ul>
                </>
              )
            )}
          </div>
        );
      })}
    </div>
  );
};


// Render volunteer work. Each entry may be either a simple string or an object
// with fields (organization, role, startDate, endDate, present, hoursPerWeek, responsibilities).
// We convert objects into a human‑readable multi‑line block rather than
// blindly calling String() which would produce "[object Object]". Plain
// strings are still rendered as simple list items.
const renderVolunteer = (vols = [], federal = true) => {
  if (!isNonEmptyArray(vols)) return null;
  // Determine if any element is an object; if so, use rich formatting
  const hasObject = vols.some((v) => v && typeof v === 'object' && !Array.isArray(v));
  if (!hasObject) {
    // Fallback: simple list of strings
    const items = vols.map(cleanText).filter(Boolean);
    if (!items.length) return null;
    return <ul>{items.map((v, i) => <li key={i}>{v}</li>)}</ul>;
  }
  // Otherwise, render each object with multiple lines
  return (
    <div>
      {vols.map((vol, idx) => {
        if (!vol) return null;
        if (typeof vol !== 'object') {
          const text = cleanText(vol);
          return text ? <div key={idx}><li>{text}</li></div> : null;
        }
        const organization = cleanText(vol.organization);
        const role = cleanText(vol.role);
        const startDate = cleanText(vol.startDate);
        const endDate = cleanText(vol.endDate);
        const present = !!vol.present;
        const hours = cleanText(vol.hoursPerWeek);
        const responsibilities = cleanText(vol.responsibilities);
        const titleLine = [
          role,
          organization ? `— ${organization}` : null,
        ].filter(Boolean).join(' ');
        const dateLine = formatResumeDateRange(startDate, endDate, present);
        return (
          <div key={idx} style={{ marginBottom: 8 }}>
            {isNonEmptyString(titleLine) && <div><strong>{titleLine}</strong></div>}
            {isNonEmptyString(dateLine) && <div className="right-align">{dateLine}</div>}
            {federal && isNonEmptyString(hours) && <div>Hours/Week: {hours}</div>}
            {isNonEmptyString(responsibilities) && <div>{responsibilities}</div>}
          </div>
        );
      })}
    </div>
  );
};

const renderReferences = (refs = []) => {
  if (!isNonEmptyArray(refs)) return null;
  return (
    <ul className="uniform-line-spacing-references">
      {refs.map((r, i) => {
        if (!r) return null;
        const name = cleanText(r.name);
        const phone = cleanText(r.phoneNumber || r.phone);
        const email = cleanText(r.email);
        const relationship = cleanText(r.relationship);
        const line = [
          name,
          phone,
          email,
          relationship ? `(${relationship})` : null
        ].filter(Boolean).join(' • ');
        return <li key={i}>{line}</li>;
      })}
    </ul>
  );
};

const renderAdditionalInfo = (text) => {
  const t = cleanText(text);
  if (!t) return null;
  return <p>{t}</p>;
};

const renderMilitary = (mil = {}) => {
  if (!mil) return null;

  const status = toLabel(mil.militaryStatus, STATUS_LABELS);

  // If no status selected, bail out early (don’t show branch, rank, etc.)
  if (!isNonEmptyString(status)) return null;

  const branch = toLabel(mil.militaryBranch, BRANCH_LABELS);
  const rank = cleanText(mil.militaryRank);
  const occupation = cleanText(mil.militaryOccupation);
  const location = cleanText(mil.militaryLocation);
  const duration = cleanText(mil.militaryDuration);

  const line1 = [status, branch, rank,duration,occupation,location].filter(isNonEmptyString).join(' | ');
  // const line2 = [occupation, location].filter(isNonEmptyString).join(' | ');

  // if (!line1 && !line2) return null;
  if (!line1) return null;

  return (
    <div className="military-info">
      {isNonEmptyString(line1) && <div>{line1}</div>}
      {/* {isNonEmptyString(line2) && <div>{line2}</div>} */}
    </div>
  );
};


const byOrder = (formData) => {
  const order = Array.isArray(formData?.sectionOrder) && formData.sectionOrder.length
    ? formData.sectionOrder
    : DEFAULT_ORDER;
  const known = new Set(DEFAULT_ORDER);
  return order.filter((s) => known.has(s));
};

const Resume = ({ formData, editable = false, setFormData }) => {
  const order = byOrder(formData);
  const federal = isFederalResume(formData);
  const sections = {
    personalInfo: {
      title: null, // header handles it
      render: () => null
    },
    professionalSummary: {
      title: 'Professional Summary',
      render: () => renderProfessionalSummary(formData.professionalSummary, editable, setFormData, formData)
    },
    skills: {
      title: 'Skills',
      render: () => renderSkills(formData.skills, editable, setFormData, formData)
    },
    workHistory: {
      title: 'Work Experience',
      render: () => renderWorkHistory(formData.workHistory, editable, setFormData, formData, federal)
    },
    educationHistory: {
      title: 'Education',
      render: () => renderEducation(formData.educationHistory, editable, setFormData, formData)
    },
    certifications: {
      title: 'Certifications',
      render: () => renderCertifications(formData.certifications /*, editable, setFormData, formData */)
    },
    training: {
      title: 'Training',
      render: () => renderTraining(formData.training, editable, setFormData, formData)
    },
    volunteerWork: {
      title: 'Volunteer Experience',
      render: () => renderVolunteer(formData.volunteerWork, federal)
    },
    languages: {
      title: 'Languages',
      render: () => renderLanguages(formData.languages, editable, setFormData, formData)
    },
    interests: {
      title: 'Interests & Hobbies',
      render: () => renderInterests(formData.interests, editable, setFormData, formData)
    },
    awards: {
      title: 'Awards & Honors',
      render: () => renderAwards(formData.awards, editable, setFormData, formData)
    },
    organizations: {
      title: 'Organizations',
      render: () => renderOrganizations(formData.organizations, editable, setFormData, formData)
    },
    references: {
      title: 'References',
      render: () => renderReferences(formData.references)
    },
    additionalInformation: {
      title: 'Additional Information',
      render: () => renderAdditionalInfo(formData.additionalInformation)
    },
    militaryInfo: {
      title: 'Military Service',
      render: () => renderMilitary(formData.militaryInfo)
    }
  };

  return (
    <div>
      <Header personalInfo={formData.personalInfo} federal={federal} />
      {order.map((key) => {
        if (key === 'personalInfo') return null; // header already rendered
        const sec = sections[key];
        if (!sec) return null;
        const content = sec.render?.();
        if (!content) return null; // auto-hide empty sections
        return (
          <Section key={key} title={sec.title}>
            {content}
          </Section>
        );
      })}
    </div>
  );
};

export default Resume;
